//#region header */
/**************************************************************************************************
//
//  Description: Hook to post organisations.
//
//  Copyright:    © 2026 Idox Software Limited
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
//#region Version 1.0.0.0 changes
//    001   05.02.26 Sean Flook         GMSCM-12 Initial Revision.
//#endregion Version 1.0.0.0 changes
//
//--------------------------------------------------------------------------------------------------
//#endregion header */

import useUserQueryStore from "../context/UserStore";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import SecurityAPIClient from "../services/securityApiClient";

interface PostOrganisationsContext {
  previousOrganisations: string[];
}

const usePostOrganisations = (onUpdate: (newData: string[]) => void) => {
  const queryClient = useQueryClient();
  const currentToken = useUserQueryStore((s) => s.userQuery.currentToken);
  const securityApiClient = new SecurityAPIClient<string>("/Organisation", currentToken!);

  return useMutation<string[], Error, string[], PostOrganisationsContext>({
    mutationFn: securityApiClient.post,
    onMutate: (newOrganisations) => {
      const previousOrganisations = queryClient.getQueryData<string[]>(["organisationNames"]) || [];
      queryClient.setQueryData<string[]>(["organisations"], (organisations = []) => [
        ...newOrganisations,
        ...organisations,
      ]);

      return { previousOrganisations: previousOrganisations };
    },
    onSuccess: (savedOrganisations, newOrganisations) => {
      queryClient.setQueryData<string[]>(["organisations"], newOrganisations);

      onUpdate(newOrganisations);
    },
    onError: (error, newOrganisations, context) => {
      if (!context) return;

      queryClient.setQueryData<string[]>(["organisations"], context.previousOrganisations);
    },
  });
};

export default usePostOrganisations;
